import { useState, useEffect, useRef } from 'react';
import { Table, Button, Space, message, Input, Tag, Form, Modal } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { SearchOutlined, SolutionOutlined, ReloadOutlined, EyeOutlined } from '@ant-design/icons';
import { renderDateTime, ActionButtons } from './utils';
import { ModalForm, ProFormText } from '@ant-design/pro-components';
import { Upload, CheckCircle } from 'lucide-react';

interface User {
  id: number;
  account: string;
  username: string;
  company_name: string;
  phone: string;
  email: string;
  type: string;
  create_time: string;
  // complete-info 字段
  establish_date?: string;
  register_address?: string;
  authorized_representative_name?: string;
  authorized_representative_id_card?: string;
  authorized_representative_id_card_front?: string;
  authorized_representative_id_card_back?: string;
  legal_person_name?: string;
  legal_person_id_card?: string;
  legal_person_id_card_front?: string;
  legal_person_id_card_back?: string;
  business_license?: string;
  authorization_letter?: string;
}

type FileFieldKey = 'authorized_representative_id_card_front' | 'authorized_representative_id_card_back' | 'legal_person_id_card_front' | 'legal_person_id_card_back' | 'business_license' | 'authorization_letter';

interface FileUploadFieldProps {
  value?: string;
  onChange?: (url: string) => void;
  field: FileFieldKey;
  label: string;
  isUploading: boolean;
  setUploading: (updater: (prev: Record<string, boolean>) => Record<string, boolean>) => void;
  fileNames: Record<string, string>;
  setFileNames: (updater: (prev: Record<string, string>) => Record<string, string>) => void;
  sampleUrl?: string;
  downloadUrl?: string;
  downloadName?: string;
}

function FileUploadField({ value, onChange, field, label, isUploading, setUploading, fileNames, setFileNames, sampleUrl, downloadUrl, downloadName }: FileUploadFieldProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewImage, setPreviewImage] = useState('');
  const [pdfPreviewVisible, setPdfPreviewVisible] = useState(false);
  const [pdfUrl, setPdfUrl] = useState('');
  const displayName = fileNames[field] || (value ? value.split('/').pop() || '已上传' : '');
  const isImage = displayName && /\.(jpg|jpeg|png|gif|bmp|webp)$/i.test(displayName);
  const isPdf = displayName && /\.pdf$/i.test(displayName);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading((prev) => ({ ...prev, [field]: true }));
    try {
      const token = localStorage.getItem('admin_token');
      const formData = new FormData();
      formData.append('file', file);
      const resp = await fetch('/api/admin/upload', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData,
      }).then((r) => r.json());
      if (resp?.ok && resp.url) {
        onChange?.(resp.url);
        setFileNames((prev) => ({ ...prev, [field]: file.name }));
      } else {
        message.error(resp?.error || '上传失败');
      }
    } catch {
      message.error('上传失败');
    } finally {
      setUploading((prev) => ({ ...prev, [field]: false }));
    }
    e.target.value = '';
  };

  const handlePreview = () => {
    if (value) {
      const imageUrl = value.startsWith('http') ? value : value.startsWith('/') ? value : `/api${value}`;
      setPreviewImage(imageUrl);
      setPreviewVisible(true);
    }
  };

  const handlePdfPreview = () => {
    if (value) {
      const fileUrl = value.startsWith('http') ? value : value.startsWith('/') ? value : `/api${value}`;
      setPdfUrl(fileUrl);
      setPdfPreviewVisible(true);
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, alignItems: 'center' }}>
        <label style={{ fontSize: 14, fontWeight: 500, color: '#374151' }}>{label}</label>
        {(sampleUrl || downloadUrl) && (
          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            {sampleUrl && (
              <a href={sampleUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#003c8c', fontSize: 13 }}>查看样例</a>
            )}
            {downloadUrl && (
              <a href={downloadUrl} download={downloadName} style={{ color: '#003c8c', fontSize: 13 }}>下载授权委托书</a>
            )}
          </div>
        )}
      </div>
      <div
        onClick={() => !isUploading && fileInputRef.current?.click()}
        style={{
          border: `2px dashed ${value ? '#10b981' : '#e5e7eb'}`,
          borderRadius: 12,
          height: 140,
          padding: '0 16px',
          cursor: isUploading ? 'wait' : 'pointer',
          backgroundColor: value ? '#f0fdf4' : '#fafafa',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <input ref={fileInputRef} type="file" accept="image/*,.pdf" onChange={handleFileSelect} style={{ display: 'none' }} disabled={isUploading} />
        {isUploading ? (
          <div style={{ color: '#6b7280', textAlign: 'center' }}>
            <div style={{ width: 32, height: 32, border: '3px solid #e5e7eb', borderTop: '3px solid #003c8c', borderRadius: '50%', margin: '0 auto 8px', animation: 'spin 1s linear infinite' }} />
            上传中...
          </div>
        ) : value ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                <CheckCircle style={{ width: 18, height: 18, color: '#10b981' }} />
                <span style={{ fontSize: 14, color: '#10b981', fontWeight: 600 }}>上传成功</span>
              </div>
              <div style={{ fontSize: 13, color: '#374151', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{displayName}</div>
              <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 4 }}>点击重新上传</div>
            </div>
            {isImage && (
              <div style={{ position: 'relative', width: 120, height: 90, borderRadius: 8, border: '1px solid #e5e7eb', overflow: 'hidden', flexShrink: 0 }}>
                <img
                  src={value.startsWith('http') ? value : value.startsWith('/') ? value : `/api${value}`}
                  alt={label}
                  style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                  onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                />
                <div
                  onClick={(e) => {
                    e.stopPropagation();
                    handlePreview();
                  }}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    opacity: 0,
                    transition: 'opacity 0.3s ease',
                    cursor: 'pointer'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.opacity = '1';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.opacity = '0';
                  }}
                >
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, color: 'white' }}>
                    <EyeOutlined />
                    <span style={{ fontSize: 12 }}>预览</span>
                  </div>
                </div>
              </div>
            )}
            {isPdf && (
              <a
                onClick={(e) => {
                  e.stopPropagation();
                  handlePdfPreview();
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '8px 16px',
                  backgroundColor: '#3b82f6',
                  color: 'white',
                  borderRadius: 8,
                  fontSize: 13,
                  fontWeight: 500,
                  textDecoration: 'none',
                  cursor: 'pointer',
                  flexShrink: 0,
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#2563eb';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#3b82f6';
                }}
              >
                <EyeOutlined style={{ width: 14, height: 14 }} />
                预览
              </a>
            )}
          </div>
        ) : (
          <div style={{ textAlign: 'center' }}>
            <Upload style={{ width: 28, height: 28, color: '#9ca3af', margin: '0 auto 8px', display: 'block' }} />
            <div style={{ fontSize: 14, color: '#6b7280' }}>点击上传 {label}</div>
            <div style={{ fontSize: 12, color: '#9ca3af' }}>支持 JPG、PNG、PDF</div>
          </div>
        )}
      </div>

      {/* 图片预览模态框 */}
      <Modal
        open={previewVisible}
        onCancel={() => setPreviewVisible(false)}
        footer={null}
        style={{
          top: 20,
          marginLeft: 10 // 与编辑窗口保持至少10px的空白
        }}
        maskStyle={{
          zIndex: 1000 // 确保在编辑窗口之上
        }}
        modalRender={(children: React.ReactNode) => (
          <div style={{ zIndex: 1001, position: 'relative' }}>{children}</div>
        )}
      >
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <img
            src={previewImage}
            alt="预览"
            style={{ maxWidth: '100%', maxHeight: 600, objectFit: 'contain' }}
          />
        </div>
      </Modal>

      {/* PDF预览模态框 */}
      <Modal
        title="文件预览"
        open={pdfPreviewVisible}
        onCancel={() => setPdfPreviewVisible(false)}
        footer={null}
        width={900}
        style={{
          top: 20,
          marginLeft: 10
        }}
        maskStyle={{
          zIndex: 1000
        }}
        modalRender={(children: React.ReactNode) => (
          <div style={{ zIndex: 1002, position: 'relative' }}>{children}</div>
        )}
      >
        {pdfUrl && (
          <iframe
            src={pdfUrl}
            style={{ width: '100%', height: '70vh', border: 'none' }}
            title="PDF预览"
          />
        )}
      </Modal>
    </div>
  );
}

// 状态映射：数据库值 → 前端显示值
const getStatusDisplay = (dbStatus: string): string => {
  const statusMap: Record<string, string> = {
    '预注册': '待审核',
    '审核中': '审核中',
    '供应商': '审核通过',
    '审批不通过': '审核不通过',
  };
  return statusMap[dbStatus] || dbStatus;
};

export default function AccountSupplier() {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingItem, setEditingItem] = useState<User | null>(null);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [form] = Form.useForm();
  const [uploading, setUploading] = useState<Record<string, boolean>>({});
  const [fileNames, setFileNames] = useState<Record<string, string>>({});

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const resp = await fetch('/api/admin/users/suppliers', {
        headers: { 'Authorization': `Bearer ${token}` },
      }).then(r => r.json());

      if (resp && resp.ok) {
        setUsers(resp.data || []);
      } else {
        message.error(resp?.error || '获取供应商列表失败');
      }
    } catch (err) {
      message.error('获取供应商列表失败');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (record: User) => {
    setEditingItem(record);
    const establishDate = record.establish_date ? String(record.establish_date).split('T')[0] : undefined;
    form.setFieldsValue({
      account: record.account,
      username: record.username,
      company_name: record.company_name,
      phone: record.phone,
      email: record.email,
      establish_date: establishDate,
      register_address: record.register_address,
      authorized_representative_name: record.authorized_representative_name,
      authorized_representative_id_card: record.authorized_representative_id_card,
      authorized_representative_id_card_front: record.authorized_representative_id_card_front,
      authorized_representative_id_card_back: record.authorized_representative_id_card_back,
      legal_person_name: record.legal_person_name,
      legal_person_id_card: record.legal_person_id_card,
      legal_person_id_card_front: record.legal_person_id_card_front,
      legal_person_id_card_back: record.legal_person_id_card_back,
      business_license: record.business_license,
      authorization_letter: record.authorization_letter,
    });
    setFileNames({});
    setUploading({});
    setModalVisible(true);
  };

  const handleDelete = async (id: number) => {
    try {
      const token = localStorage.getItem('admin_token');
      const resp = await fetch(`/api/admin/users/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      }).then(r => r.json());

      if (resp && resp.ok) {
        message.success('删除成功');
        fetchUsers();
      } else {
        message.error(resp?.error || '删除失败');
      }
    } catch (err) {
      message.error('删除失败');
    }
  };

  const handleSubmit = async (values: any) => {
    try {
      const token = localStorage.getItem('admin_token');

      // 邮箱查重：编辑时邮箱未变则跳过；否则以数据库最新数据为准判断是否重复
      const nextEmail = String(values?.email || '').trim();
      const prevEmail = String(editingItem?.email || '').trim();
      if (editingItem?.id && nextEmail && nextEmail !== prevEmail) {
        const listResp = await fetch('/api/admin/users/suppliers', {
          headers: { 'Authorization': `Bearer ${token}` },
        }).then(r => r.json());

        if (listResp?.ok) {
          const list: User[] = listResp.data || [];
          const duplicated = list.some(u =>
            u?.id !== editingItem.id &&
            String(u?.email || '').trim().toLowerCase() === nextEmail.toLowerCase()
          );
          if (duplicated) {
            message.error('该邮箱已存在，请更换邮箱后再提交');
            return false;
          }
        }
      }
      
      const resp = await fetch(`/api/admin/users/${editingItem?.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(values),
      }).then(r => r.json());

      if (resp && resp.ok) {
        message.success('更新成功');
        setModalVisible(false);
        fetchUsers();
        return true;
      } else {
        message.error(resp?.error || '操作失败');
        return false;
      }
    } catch (err) {
      message.error('操作失败');
      return false;
    }
  };

  // 过滤数据
  const filteredData = users.filter(u => 
    u.account?.toLowerCase().includes(searchKeyword.toLowerCase()) ||
    u.username?.toLowerCase().includes(searchKeyword.toLowerCase()) ||
    u.company_name?.toLowerCase().includes(searchKeyword.toLowerCase()) ||
    u.phone?.includes(searchKeyword)
  );

  const columns: ColumnsType<User> = [
    {
      title: '纳税人识别号',
      dataIndex: 'account',
      key: 'account',
      width: 180,
      render: (text) => <span style={{ fontFamily: 'monospace', color: '#1f2937' }}>{text}</span>
    },
    {
      title: '公司名称',
      dataIndex: 'company_name',
      key: 'company_name',
      ellipsis: true,
      render: (text) => <span style={{ fontWeight: 500, color: '#1f2937' }}>{text}</span>
    },
    {
      title: '联系人',
      dataIndex: 'username',
      key: 'username',
      width: 100,
    },
    {
      title: '联系电话',
      dataIndex: 'phone',
      key: 'phone',
      width: 130,
    },
    {
      title: '邮箱',
      dataIndex: 'email',
      key: 'email',
      width: 180,
      ellipsis: true,
    },
    {
      title: '状态',
      dataIndex: 'type',
      key: 'type',
      width: 100,
      render: (type: string) => {
        const displayStatus = getStatusDisplay(type);
        const colorMap: Record<string, string> = {
          '审核通过': 'success',
          '审核中': 'processing',
          '待审核': 'warning',
          '审核不通过': 'error',
        };
        return <Tag color={colorMap[displayStatus] || 'default'}>{displayStatus}</Tag>;
      }
    },
    {
      title: '创建时间',
      dataIndex: 'create_time',
      key: 'create_time',
      width: 170,
      render: renderDateTime,
    },
    {
      title: '操作',
      key: 'action',
      width: 150,
      fixed: 'right' as const,
      render: (_, record) => (
        <ActionButtons
          onEdit={() => handleEdit(record)}
          onDelete={() => handleDelete(record.id)}
          deleteTitle="请确定真的要删除这个企业，我们将从物理上彻底删除该企业注册信息，删除后该企业可以用这个联系人重新注册"
        />
      ),
    },
  ];

  return (
    <div className="admin-page">
      <div className="admin-page-header">
        <div className="admin-page-title">
          <div className="admin-page-title-icon">
            <SolutionOutlined />
          </div>
          <div>
            <h2>供应商管理</h2>
            <p>管理所有供应商账号</p>
          </div>
        </div>
        
        <Space size="middle">
          <Input
            placeholder="搜索供应商..."
            prefix={<SearchOutlined />}
            value={searchKeyword}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchKeyword(e.target.value)}
            style={{ width: 220, borderRadius: 10 }}
            allowClear
          />
          <Button 
            icon={<ReloadOutlined />} 
            onClick={fetchUsers}
            style={{ borderRadius: 10 }}
          >
            刷新
          </Button>
        </Space>
      </div>
      
      <div className="admin-page-content">
        <div style={{ marginBottom: 16, color: '#6b7280', fontSize: 14 }}>
          共 <span style={{ color: '#3b82f6', fontWeight: 600 }}>{filteredData.length}</span> 条记录
        </div>
        
        <Table
          columns={columns}
          dataSource={filteredData}
          loading={loading}
          rowKey="id"
          className="admin-table"
          scroll={{ x: 'max-content' }}
          pagination={{
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total: number, range: [number, number]) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`,
          }}
        />
      </div>
      
      <ModalForm
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 36,
              height: 36,
              borderRadius: 10,
              background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <SolutionOutlined />
            </div>
            <span>编辑供应商</span>
          </div>
        }
        open={modalVisible}
        onOpenChange={setModalVisible}
        form={form}
        onFinish={handleSubmit}
        width={720}
        modalProps={{
          destroyOnHidden: true,
          centered: true,
          bodyStyle: { maxHeight: '70vh', overflowY: 'auto' },
        }}
        submitter={{
          searchConfig: { submitText: '确定', resetText: '取消' },
        }}
      >
        <div style={{ marginTop: 24 }}>
          <ProFormText
            name="account"
            label="纳税人识别号"
            placeholder="请输入纳税人识别号"
            rules={[{ required: true, message: '请输入纳税人识别号' }]}
          />
          <ProFormText
            name="company_name"
            label="公司名称"
            placeholder="请输入公司名称"
            rules={[{ required: true, message: '请输入公司名称' }]}
          />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <ProFormText name="username" label="联系人" placeholder="请输入联系人" />
            <ProFormText name="phone" label="联系电话" placeholder="请输入联系电话" />
          </div>
          <ProFormText
            name="email"
            label="邮箱"
            placeholder="请输入邮箱"
            rules={[
              { required: true, message: '请输入邮箱' },
              { type: 'email', message: '请输入正确的邮箱格式' },
            ]}
          />
          <ProFormText
            name="password"
            label="新密码"
            placeholder="如需修改密码请输入"
            fieldProps={{ type: 'password' }}
          />

          <div style={{ marginTop: 24, paddingTop: 16, borderTop: '1px solid #f0f0f0' }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: '#1f2937', marginBottom: 12 }}>基本信息（与完善信息一致）</div>
            <Form.Item name="establish_date" label="成立时间">
              <Input type="date" style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="register_address" label="注册地址">
              <Input placeholder="请输入注册地址" />
            </Form.Item>
          </div>

          <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid #f0f0f0' }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: '#1f2937', marginBottom: 12 }}>授权代表信息</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <ProFormText name="authorized_representative_name" label="授权代表姓名" placeholder="请输入" />
              <ProFormText name="authorized_representative_id_card" label="授权代表身份证号" placeholder="请输入18位身份证号" />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <Form.Item name="authorized_representative_id_card_front" label="授权代表身份证正面">
                <FileUploadField
                  field="authorized_representative_id_card_front"
                  label="授权代表身份证正面"
                  isUploading={uploading.authorized_representative_id_card_front || false}
                  setUploading={setUploading}
                  fileNames={fileNames}
                  setFileNames={setFileNames}
                  sampleUrl="/upload/example/idcard_front.png"
                />
              </Form.Item>
              <Form.Item name="authorized_representative_id_card_back" label="授权代表身份证背面">
                <FileUploadField
                  field="authorized_representative_id_card_back"
                  label="授权代表身份证背面"
                  isUploading={uploading.authorized_representative_id_card_back || false}
                  setUploading={setUploading}
                  fileNames={fileNames}
                  setFileNames={setFileNames}
                  sampleUrl="/upload/example/idcard_back.png"
                />
              </Form.Item>
            </div>
          </div>

          <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid #f0f0f0' }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: '#1f2937', marginBottom: 12 }}>法人信息</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <ProFormText name="legal_person_name" label="法人姓名" placeholder="请输入" />
              <ProFormText name="legal_person_id_card" label="法人身份证号" placeholder="请输入18位身份证号" />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <Form.Item name="legal_person_id_card_front" label="法人身份证正面">
                <FileUploadField
                  field="legal_person_id_card_front"
                  label="法人身份证正面"
                  isUploading={uploading.legal_person_id_card_front || false}
                  setUploading={setUploading}
                  fileNames={fileNames}
                  setFileNames={setFileNames}
                  sampleUrl="/upload/example/idcard_front.png"
                />
              </Form.Item>
              <Form.Item name="legal_person_id_card_back" label="法人身份证背面">
                <FileUploadField
                  field="legal_person_id_card_back"
                  label="法人身份证背面"
                  isUploading={uploading.legal_person_id_card_back || false}
                  setUploading={setUploading}
                  fileNames={fileNames}
                  setFileNames={setFileNames}
                  sampleUrl="/upload/example/idcard_back.png"
                />
              </Form.Item>
            </div>
          </div>

          <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid #f0f0f0' }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: '#1f2937', marginBottom: 12 }}>资质文件</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <Form.Item name="business_license" label="营业执照">
                <FileUploadField
                  field="business_license"
                  label="营业执照"
                  isUploading={uploading.business_license || false}
                  setUploading={setUploading}
                  fileNames={fileNames}
                  setFileNames={setFileNames}
                  sampleUrl="/upload/example/account.png"
                />
              </Form.Item>
              <Form.Item name="authorization_letter" label="授权委托书">
                <FileUploadField
                  field="authorization_letter"
                  label="授权委托书"
                  isUploading={uploading.authorization_letter || false}
                  setUploading={setUploading}
                  fileNames={fileNames}
                  setFileNames={setFileNames}
                  sampleUrl="/upload/example/shouquan.png"
                  downloadUrl="/upload/templates/authorization.docx"
                  downloadName="授权委托书.docx"
                />
              </Form.Item>
            </div>
          </div>
        </div>
      </ModalForm>
    </div>
  );
}
